import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { FORM_CONTROL_FOCUS_CLASS } from '@/components/ui/form-control-styles'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { Permit } from '@/components/guards/Permit'
import { usePermissions } from '@/hooks/usePermissions'
import { ReminderIntervalsInput } from '@/components/settings/documents/ReminderIntervalsInput'
import {
  useInvoiceSettings,
  useQuoteSettings,
  useUpdateInvoiceSettings,
  useUpdateQuoteSettings,
} from '@/hooks/useSettings'
import { toast } from '@/components/ui/toast'
import { getApiErrorMessage } from '@/services/api'
import {
  DEFAULT_INVOICE_SETTINGS_FORM_STATE,
  normalizeReminderIntervals,
} from '@/lib/invoiceSettings'
import type { InvoiceSettingsFormState } from '@/types/settings'

const fieldInputClass = cn(
  'h-10 rounded-xl bg-[#f4f5f8] px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] shadow-none placeholder:text-[#6c7285]',
  FORM_CONTROL_FOCUS_CLASS
)
const readOnlyInputClass = 'bg-[#eef1f6] text-[#6c7285]'
const sectionCardClass =
  'overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_8px_16px_-4px_rgba(38,45,66,0.1),0_1px_4px_-4px_rgba(38,45,66,0.05)]'
const sectionCardBodyClass = 'flex flex-col gap-5 px-6 pb-6 pt-4'
const saveButtonClass =
  'h-10 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-4 text-sm font-medium leading-none text-white shadow-none hover:bg-[#e6511c]'
const stickyFooterClass =
  'sticky bottom-0 z-20 mt-auto -mx-6 -mb-6 flex h-[72px] items-center justify-end border-t border-[#e7e9ef] bg-white px-6 shadow-[0_-1px_4px_0_rgba(38,45,66,0.05)]'

const switchClass =
  'h-6 w-10 data-[state=checked]:bg-[#ff5a1f] data-[state=unchecked]:bg-[#d9d9d9] [&>span]:h-5 [&>span]:w-5 [&>span]:data-[state=checked]:translate-x-4 [&>span]:data-[state=unchecked]:translate-x-0.5'

interface QuoteState {
  numbering_format: string
  default_expiry_period: string
}

const DEFAULT_QUOTE: QuoteState = {
  numbering_format: 'QUO-YYYY-',
  default_expiry_period: '30',
}

function FieldLabel({
  htmlFor,
  required,
  children,
}: {
  htmlFor?: string
  required?: boolean
  children: ReactNode
}) {
  return (
    <Label
      htmlFor={htmlFor}
      className='flex items-center gap-1 text-xs font-medium leading-4 text-[#171a20]'
    >
      {children}
      {required && <span className='text-[#ec221f]'> *</span>}
    </Label>
  )
}

function SectionCard({
  title,
  children,
}: {
  title: string
  children: ReactNode
}) {
  return (
    <section className={sectionCardClass}>
      <header className='flex items-center gap-2 px-0 pb-2 pt-6 pr-6'>
        <div className='flex min-w-0 flex-1 items-center border-l-4 border-[#ff5a1f] px-6 py-1'>
          <h2 className='font-heading text-sm font-semibold leading-[1.2] text-[#171a20]'>
            {title}
          </h2>
        </div>
      </header>
      <div className={sectionCardBodyClass}>{children}</div>
    </section>
  )
}

// 2.11.12 + 2.11.13 — Quote and invoice numbering settings on one page.
export function DocumentNumberingPanel() {
  const { t } = useTranslation('settings')
  const { canEdit } = usePermissions()
  const readOnly = !canEdit('settings')
  const {
    data: quoteData,
    isLoading: quoteLoading,
    isError: quoteError,
  } = useQuoteSettings()
  const {
    data: invoiceData,
    isLoading: invoiceLoading,
    isError: invoiceError,
  } = useInvoiceSettings()
  const updateQuote = useUpdateQuoteSettings()
  const updateInvoice = useUpdateInvoiceSettings()

  const [quoteState, setQuoteState] = useState<QuoteState>(DEFAULT_QUOTE)
  const [invoiceState, setInvoiceState] = useState<InvoiceSettingsFormState>(
    DEFAULT_INVOICE_SETTINGS_FORM_STATE
  )
  const [quoteErrors, setQuoteErrors] = useState<Record<string, string>>({})
  const [invoiceErrors, setInvoiceErrors] = useState<Record<string, string>>({})

  const isLoading = quoteLoading || invoiceLoading
  const isError = quoteError || invoiceError
  const isSaving = updateQuote.isPending || updateInvoice.isPending

  useEffect(() => {
    const qs = quoteData?.data?.quote_settings as
      | Partial<QuoteState>
      | undefined
    if (!qs) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setQuoteState(s => ({
      ...s,
      ...qs,
      default_expiry_period:
        qs.default_expiry_period != null
          ? String(qs.default_expiry_period)
          : s.default_expiry_period,
    }))
  }, [quoteData])

  useEffect(() => {
    const is = invoiceData?.data?.invoice_settings as
      | Record<string, unknown>
      | undefined
    if (!is) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setInvoiceState(s => ({
      ...s,
      ...is,
      default_payment_terms: String(
        is.default_payment_terms ?? s.default_payment_terms
      ),
      reminder_intervals: normalizeReminderIntervals(is.reminder_intervals),
    }))
  }, [invoiceData])

  const setQuote = (patch: Partial<QuoteState>) =>
    setQuoteState(s => ({ ...s, ...patch }))
  const setInvoice = (patch: Partial<InvoiceSettingsFormState>) =>
    setInvoiceState(s => ({ ...s, ...patch }))

  async function save() {
    const nextQuote: Record<string, string> = {}
    if (!quoteState.numbering_format.trim())
      nextQuote.numbering_format = t(
        'quoteSettings.validation.numberingRequired'
      )
    const exp = Number(quoteState.default_expiry_period)
    if (
      quoteState.default_expiry_period === '' ||
      Number.isNaN(exp) ||
      exp <= 0
    )
      nextQuote.default_expiry_period = t(
        'quoteSettings.validation.expiryPositive'
      )
    setQuoteErrors(nextQuote)

    const nextInvoice: Record<string, string> = {}
    if (!invoiceState.numbering_format.trim())
      nextInvoice.numbering_format = t(
        'invoiceSettings.validation.numberingRequired'
      )
    const terms = Number(invoiceState.default_payment_terms)
    if (
      invoiceState.default_payment_terms === '' ||
      Number.isNaN(terms) ||
      terms <= 0
    )
      nextInvoice.default_payment_terms = t(
        'invoiceSettings.validation.paymentTermsPositive'
      )
    if (invoiceState.overdue_blocking && !invoiceState.blocking_mode)
      nextInvoice.blocking_mode = t(
        'invoiceSettings.validation.blockingModeRequired'
      )
    setInvoiceErrors(nextInvoice)

    if (Object.keys(nextQuote).length || Object.keys(nextInvoice).length) return

    try {
      await Promise.all([
        updateQuote.mutateAsync({
          quote_settings: {
            ...quoteState,
            default_expiry_period: exp,
          },
        }),
        updateInvoice.mutateAsync({
          invoice_settings: {
            ...invoiceState,
            default_payment_terms: terms,
            reminder_intervals: invoiceState.reminder_intervals,
          },
        }),
      ])
      toast({
        variant: 'success',
        title: t('documentNumbering.toast.saved'),
      })
    } catch (e) {
      toast({ variant: 'destructive', title: getApiErrorMessage(e) })
    }
  }

  if (isLoading)
    return (
      <div className='space-y-5'>
        <Skeleton className='h-[188px] rounded-2xl' />
        <Skeleton className='h-[260px] rounded-2xl' />
        <Skeleton className='h-[164px] rounded-2xl' />
      </div>
    )
  if (isError)
    return (
      <section className={sectionCardClass}>
        <div className='px-6 py-8 text-center text-sm text-destructive'>
          {t('documentNumbering.loadError')}
        </div>
      </section>
    )

  return (
    <div className='flex min-h-0 flex-1 flex-col'>
      <div className='space-y-5 pb-24'>
        <SectionCard title={t('quoteSettings.title')}>
          <div className='flex flex-col gap-4'>
            <div className='flex flex-col gap-2'>
              <FieldLabel htmlFor='qs-num' required>
                {t('quoteSettings.fields.numberingFormat')}
              </FieldLabel>
              <Input
                id='qs-num'
                value={quoteState.numbering_format}
                placeholder='QUO-YYYY-'
                readOnly={readOnly}
                onChange={e => {
                  setQuote({ numbering_format: e.target.value })
                  setQuoteErrors(s => ({ ...s, numbering_format: '' }))
                }}
                className={cn(
                  fieldInputClass,
                  readOnly && readOnlyInputClass,
                  quoteErrors.numbering_format &&
                    'border border-destructive bg-white'
                )}
              />
              <p className='text-xs font-medium leading-4 text-[#6c7285]'>
                {t('quoteSettings.numberingHelp')}
              </p>
              {quoteErrors.numbering_format && (
                <p className='text-sm text-destructive'>
                  {quoteErrors.numbering_format}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <FieldLabel htmlFor='qs-exp' required>
                {t('quoteSettings.fields.defaultExpiry')}
              </FieldLabel>
              <Input
                id='qs-exp'
                type='number'
                min='1'
                value={quoteState.default_expiry_period}
                readOnly={readOnly}
                onChange={e => {
                  setQuote({ default_expiry_period: e.target.value })
                  setQuoteErrors(s => ({ ...s, default_expiry_period: '' }))
                }}
                className={cn(
                  fieldInputClass,
                  readOnly && readOnlyInputClass,
                  quoteErrors.default_expiry_period &&
                    'border border-destructive bg-white'
                )}
              />
              {quoteErrors.default_expiry_period && (
                <p className='text-sm text-destructive'>
                  {quoteErrors.default_expiry_period}
                </p>
              )}
            </div>
          </div>
        </SectionCard>

        <SectionCard title={t('invoiceSettings.title')}>
          <div className='flex flex-col gap-4'>
            <div className='flex flex-col gap-2'>
              <FieldLabel htmlFor='is-num' required>
                {t('invoiceSettings.fields.numberingFormat')}
              </FieldLabel>
              <Input
                id='is-num'
                value={invoiceState.numbering_format}
                placeholder='INV-YYYY-'
                readOnly={readOnly}
                onChange={e => {
                  setInvoice({ numbering_format: e.target.value })
                  setInvoiceErrors(s => ({ ...s, numbering_format: '' }))
                }}
                className={cn(
                  fieldInputClass,
                  readOnly && readOnlyInputClass,
                  invoiceErrors.numbering_format &&
                    'border border-destructive bg-white'
                )}
              />
              <p className='text-xs font-medium leading-4 text-[#6c7285]'>
                {t('invoiceSettings.numberingHelp')}
              </p>
              {invoiceErrors.numbering_format && (
                <p className='text-sm text-destructive'>
                  {invoiceErrors.numbering_format}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <FieldLabel htmlFor='is-terms' required>
                {t('invoiceSettings.fields.paymentTerms')}
              </FieldLabel>
              <Input
                id='is-terms'
                type='number'
                min='1'
                value={invoiceState.default_payment_terms}
                readOnly={readOnly}
                onChange={e => {
                  setInvoice({ default_payment_terms: e.target.value })
                  setInvoiceErrors(s => ({
                    ...s,
                    default_payment_terms: '',
                  }))
                }}
                className={cn(
                  fieldInputClass,
                  readOnly && readOnlyInputClass,
                  invoiceErrors.default_payment_terms &&
                    'border border-destructive bg-white'
                )}
              />
              {invoiceErrors.default_payment_terms && (
                <p className='text-sm text-destructive'>
                  {invoiceErrors.default_payment_terms}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <FieldLabel htmlFor='is-rem'>
                {t('invoiceSettings.fields.reminderIntervals')}
              </FieldLabel>
              <ReminderIntervalsInput
                value={invoiceState.reminder_intervals}
                onChange={reminder_intervals =>
                  setInvoice({ reminder_intervals })
                }
                readOnly={readOnly}
                placeholder={t('invoiceSettings.reminderIntervalsPlaceholder')}
                error={invoiceErrors.reminder_intervals}
                inputClassName={cn(
                  fieldInputClass,
                  readOnly && readOnlyInputClass
                )}
              />
              <p className='text-xs font-medium leading-4 text-[#6c7285]'>
                {t('invoiceSettings.reminderIntervalsHelp')}
              </p>
            </div>
          </div>
        </SectionCard>

        <SectionCard title={t('invoiceSettings.fields.overdueBlocking')}>
          <div className='flex flex-col gap-4'>
            <div className='flex items-start justify-between gap-4'>
              <p className='text-sm font-medium leading-[1.4] text-[#171a20]'>
                {t('invoiceSettings.overdueBlockingTitle')}
              </p>
              <Switch
                id='is-block'
                checked={invoiceState.overdue_blocking}
                disabled={readOnly}
                onCheckedChange={v =>
                  setInvoice({
                    overdue_blocking: v,
                    blocking_mode: v ? invoiceState.blocking_mode : '',
                  })
                }
                className={switchClass}
              />
            </div>

            {invoiceState.overdue_blocking && (
              <div className='rounded-2xl bg-[#f4f5f8] p-4'>
                <div className='grid gap-3'>
                  {(
                    [
                      {
                        value: 'warning',
                        label: t('invoiceSettings.blockingModes.warning'),
                      },
                      {
                        value: 'hard',
                        label: t('invoiceSettings.blockingModes.hard'),
                      },
                    ] as const
                  ).map(opt => {
                    const checked = invoiceState.blocking_mode === opt.value
                    return (
                      <label
                        key={opt.value}
                        className={cn(
                          'flex items-center gap-3 text-sm font-medium leading-[1.4] text-[#171a20]',
                          readOnly ? 'cursor-default' : 'cursor-pointer'
                        )}
                      >
                        <span
                          className={cn(
                            'flex size-4 shrink-0 items-center justify-center rounded-full border bg-white',
                            checked ? 'border-[#ff5a1f]' : 'border-[#c9cfdb]'
                          )}
                          aria-hidden
                        >
                          {checked && (
                            <span className='size-2 rounded-full bg-[#ff5a1f]' />
                          )}
                        </span>
                        <input
                          type='radio'
                          name='blocking_mode'
                          value={opt.value}
                          checked={checked}
                          disabled={readOnly}
                          onChange={() => {
                            setInvoice({ blocking_mode: opt.value })
                            setInvoiceErrors(s => ({
                              ...s,
                              blocking_mode: '',
                            }))
                          }}
                          className='sr-only'
                        />
                        {opt.label}
                      </label>
                    )
                  })}
                </div>
              </div>
            )}

            {invoiceErrors.blocking_mode && (
              <p className='text-sm text-destructive'>
                {invoiceErrors.blocking_mode}
              </p>
            )}
          </div>
        </SectionCard>
      </div>

      <footer className={stickyFooterClass}>
        <div className='flex justify-end'>
          <Permit module='settings' action='edit'>
            <Button
              className={saveButtonClass}
              onClick={() => void save()}
              disabled={isSaving}
            >
              {t('documentNumbering.saveChanges')}
            </Button>
          </Permit>
        </div>
      </footer>
    </div>
  )
}

export default DocumentNumberingPanel
